import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { escapeRegex } from '@adornis/filter/aspect-schemas/escapeRegex.js';
import type { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { from, map, of, switchMap } from 'rxjs';
import { Group } from '../../groups/entities/Group.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { BillingPositionTemplate } from '../entities/BillingPositionTemplate.js';
import { resolveCountSepaMandatesForBillingPosition } from '../server/resolveCountSepaMandatesForBillingPosition.js';
import { resolveRenameBillingPositionOnCorrespondingSepaMandates } from '../server/resolveRenameBillingPositionOnCorrespondingSepaMandates.js';

const subscribeBillingPositionTemplates = registerSubscription({
  operationName: 'subscribeBillingPositionTemplates',
  type: () => [BillingPositionTemplate],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <Sel extends BaseQLSelectionSet<BillingPositionTemplate>>(gqlFields: Sel) => {
      const mongoFilter = filter.getMongoFilter();
      const scopes = filter.getFilterAspect<ListAspect>('scopes');

      // scopes need to be handled custom as a null value is a valid scope and means global, so the should always be included
      if (scopes) {
        mongoFilter.$and = mongoFilter.$and || [];
        mongoFilter.$and.push({
          $or: [{ scope: null }, { scope: { $exists: false } }, { scope: { $in: scopes.values } }],
        });
      }

      return watchQuery<NarrowedEntity<BillingPositionTemplate, Sel>>(
        BillingPositionTemplate._collectionName,
        mongoFilter,
        {
          sort: filter.getMongoOptions().sort,
          skip,
          limit,
          projection: gqlFields,
        },
      );
    },
});

@Entity()
export class FreeTemplateInformation extends MongoEntity {
  static override _class = 'FreeTemplateInformation';

  constructor(doc: Partial<FreeTemplateInformation>) {
    super(doc);
  }

  @Field(() => String)
  public name: Maybe<string>;

  @Field(() => String)
  public kuerzel: Maybe<string>;
}

const getAllFreeTextTemplates = registerQuery({
  operationName: 'getAllFreeTextTemplates',
  type: () => [FreeTemplateInformation],
  middlewares: [authenticatedMiddleware],
  params: [{ type: () => String, name: 'search' }],
  resolve:
    (search: string) =>
    async <Sel extends BaseQLSelectionSet<FreeTemplateInformation>>(gqlFields: Sel) => {
      // a group position without a template is a free-text one; booking overrides carry no names
      const col = await getRawCollection<EntityData<Group>>(Group._collectionName);

      const selector: Record<string, any> = {
        billingPosition: { $ne: null },
        $or: [{ 'billingPosition.templateID': null }, { 'billingPosition.templateID': { $exists: false } }],
      };

      if (search) {
        selector['billingPosition.name'] = { $regex: escapeRegex(search), $options: 'i' };
      }

      const groups = await col.find(selector, { projection: { billingPosition: 1 } }).toArray();

      const allFreeTemplates = groups
        .map(group => group.billingPosition)
        .filter(isNotNullish)
        .filter(bp => !bp.templateID);

      const uniqueTemplates = new Map<string, FreeTemplateInformation>();

      for (const bp of allFreeTemplates) {
        if (bp.kuerzel && !uniqueTemplates.has(bp.kuerzel)) {
          uniqueTemplates.set(bp.kuerzel, new FreeTemplateInformation({ name: bp.name, kuerzel: bp.kuerzel }));
        }
      }

      return Array.from(uniqueTemplates.values()) as unknown as Array<NarrowedEntity<FreeTemplateInformation, Sel>>;
    },
});

const getAllCombinedTextTemplates = registerSubscription({
  operationName: 'getAllCombinedTextTemplates',
  type: () => [FreeTemplateInformation],
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <Sel extends BaseQLSelectionSet<FreeTemplateInformation>>(gqlFields: Sel) => {
      return subscribeBillingPositionTemplates(
        skip,
        limit,
        filter,
      )(gqlFields).pipe(
        switchMap(allTemplates => {
          const allBillinPositions: FreeTemplateInformation[] = allTemplates.map(
            template => new FreeTemplateInformation({ name: template.name, kuerzel: template.kuerzel }),
          );

          if (allBillinPositions.length >= limit) {
            return of(allBillinPositions);
          }

          return from(getAllFreeTextTemplates(filter.search || '')(gqlFields)).pipe(
            map(freeTemplates => {
              allBillinPositions.push(
                ...(freeTemplates as FreeTemplateInformation[]).slice(0, limit - allBillinPositions.length),
              );

              return allBillinPositions;
            }),
          );
        }),
      );
    },
});

const countSepaMandatesForBillingPosition = registerQuery({
  operationName: 'countForBillingPosition',
  type: () => Int,
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => String, name: 'templateID' },
    { type: () => String, name: 'oldName' },
    { type: () => String, name: 'oldKuerzel' },
  ],

  resolve: resolveCountSepaMandatesForBillingPosition,
});

const renameBillingPositionOnCorrespondingSepaMandates = registerMutation({
  operationName: 'renameBillingPositionOnCorrespondingSepaMandates',
  type: () => String,
  params: [
    { type: () => String, name: 'templateID' },
    { type: () => String, name: 'oldName' },
    { type: () => String, name: 'oldKuerzel' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveRenameBillingPositionOnCorrespondingSepaMandates,
});

export const BillingPositionTemplateAPI = {
  subscribe: subscribeBillingPositionTemplates,
  getFree: getAllFreeTextTemplates,
  getCombined: getAllCombinedTextTemplates,
  countForBillingPosition: countSepaMandatesForBillingPosition,
  renameSepaAndBillingPosition: renameBillingPositionOnCorrespondingSepaMandates,
};
